import React, { useRef, useState, useMemo, useCallback, useEffect } from "react";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Form, Modal, Row, Col, InputNumber, Select } from "antd";
import { connect } from "dva";
import {
  FixedByZipPriceType,
  FixedByZipPriceTypeMap,
  FixedByZipDirectionType,
  FixedByZipDirectionTypeMap,
} from "@/utils/enum";
import { numberToPrice, formatPrice } from "@/utils/utils";
import MoreMenu from "@/components/MoreMenu";
import TableWithPagination from "@/components/TableWithPagination";
import ZipCodeInput from "@/components/ZipCodeInput";
import AlertWithBorder from "../AlertWithBorder";
import {
  EntityType,
  PricingValueTypeEnum,
  DealerStrategiesEnum,
  TransporterStrategiesEnum,
  fetchDealerStrategyByType,
  dealerStrategyInitialParamsByType,
  transporterStrategyInitialParamsByType,
} from "./constants";

const { Option } = Select;

const ZIP_CODE_REGEX = /^\d{5}(?:[-\s]\d{4})?$/;

const FixedByZip = ({ configPerm, type, inherited, strategy, dispatch }) => {
  const initialParams = useMemo(() => {
    if (type === EntityType.Dealer) {
      return dealerStrategyInitialParamsByType[DealerStrategiesEnum.FixedByZip];
    } else if (type === EntityType.Transporter) {
      return transporterStrategyInitialParamsByType[TransporterStrategiesEnum.Variable];
    }

    return null;
  }, [type]);

  const formRef = useRef(null);
  const filledRef = useRef(false);
  const lastSortingConfigFetched = useRef(initialParams);

  const [page, setPage] = useState(initialParams.page);
  const [pageSize, setPageSize] = useState(initialParams.pageSize);
  const [editingRow, setEditingRow] = useState(null);
  const [deletingRow, setDeletingRow] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [zipFrom, setZipFrom] = useState("");
  const [zipTo, setZipTo] = useState("");

  const disabled = useMemo(() => {
    return !configPerm["PricingService.PricingSettings.Update"];
  }, [configPerm]);

  const { dataSource, totalItems, defaultScenario } = useMemo(() => {
    if (!strategy) {
      return {
        dataSource: [],
        totalItems: 0,
        defaultScenario: false,
      };
    }

    const notDefaultScenario = (strategy?.items || []).find(row => row.type !== PricingValueTypeEnum.Default);

    return {
      dataSource: strategy.items,
      totalItems: strategy.totalCount || 0,
      defaultScenario: !notDefaultScenario || inherited,
    };
  }, [strategy]);

  const showDirectionField = useMemo(() => {
    return (zipFrom !== zipTo || (zipFrom === "" && zipTo === "")) && editingRow === null;
  }, [zipFrom, zipTo, editingRow]);

  const resetFormFields = useCallback(() => {
    filledRef.current = null;
    formRef.current.resetFields();
  }, []);

  const handleConfirm = useCallback(() => {
    formRef.current.validateFields().then(response => {
      let dispatchType;
      let payload = {};

      const { zipFrom, zipTo, carPrice, suvPrice, truckPrice, truckHeavyPrice, priceType } = response;

      const newRowParser = {
        [EntityType.Dealer]: "pricingStrategies/dealerFixedByZipAddRow",
        [EntityType.Transporter]: "pricingStrategies/transporterFixedByZipAddRow",
      };

      const updateRowParser = {
        [EntityType.Dealer]: "pricingStrategies/dealerFixedByZipUpdateRow",
        [EntityType.Transporter]: "pricingStrategies/transporterFixedByZipUpdateRow",
      };

      if (!editingRow) {
        dispatchType = newRowParser[type];
        payload = {
          referenceId: strategy.referenceId,
          zipFrom,
          zipTo,
          carPrice,
          suvPrice,
          truckPrice,
          truckHeavyPrice,
          direction: zipFrom === zipTo ? FixedByZipDirectionType.OneWay : response.direction,
          priceType,
          page,
          pageSize,
        };
      } else {
        dispatchType = updateRowParser[type];
        payload = {
          referenceId: strategy.referenceId,
          zipFrom,
          zipTo,
          carPrice,
          suvPrice,
          truckPrice,
          truckHeavyPrice,
          priceType,
          id: editingRow.id,
          concurrencyStamp: editingRow.concurrencyStamp,
          page,
          pageSize,
        };
      }

      if (dispatchType !== undefined) {
        setLoading(true);
        dispatch({
          type: dispatchType,
          ...payload,
        }).then(success => {
          setLoading(false);
          if (success) {
            setModalOpen(false);
            setZipFrom("");
            setZipTo("");
            setEditingRow(null);
          }
        });
      }
    });
  }, [type, strategy, editingRow, page, pageSize]);

  const handleCreateCancel = useCallback(() => {
    resetFormFields();
    setModalOpen(false);
    setZipFrom("");
    setZipTo("");
    setEditingRow(null);
  }, [resetFormFields]);

  const onAddRow = useCallback(() => {
    setEditingRow(null);
    setModalOpen(true);
  }, []);

  const validateZipCode = useCallback((_, value) => {
    return ZIP_CODE_REGEX.test(value) ? Promise.resolve() : Promise.reject("Please enter a valid zip code");
  }, []);

  const handleDelete = useCallback(() => {
    let newPage = page;
    if (strategy.totalCount - 1 === page * pageSize && newPage > 0) {
      newPage--;

      lastSortingConfigFetched.current = {
        page: newPage,
        pageSize,
      };

      setPage(newPage);
    }

    setLoadingDelete(true);

    const dispatchTypeParser = {
      [EntityType.Dealer]: "pricingStrategies/dealerFixedByZipDeleteRow",
      [EntityType.Transporter]: "pricingStrategies/transporterFixedByZipDeleteRow",
    };

    dispatch({
      type: dispatchTypeParser[type],
      referenceId: strategy.referenceId,
      rowId: deletingRow.id,
      page: newPage,
      pageSize,
    }).finally(() => {
      setLoadingDelete(false);
      setDeletingRow(null);
    });
  }, [type, deletingRow, strategy.referenceId, strategy.totalCount, page, pageSize]);

  const handleCancelDelete = useCallback(() => {
    setDeletingRow(null);
    setEditingRow(null);
    filledRef.current = null;
  }, []);

  const onMenuItemClick = useCallback((key, row) => {
    if (key === "edit") {
      if (filledRef.current !== row.id) {
        filledRef.current = null;
      }

      setEditingRow(row);
      setModalOpen(true);
    } else if (key === "delete") {
      setDeletingRow(row);
    }
  }, []);

  const columns = useMemo(() => {
    return [
      { title: "Zip from", dataIndex: "originZipCode" },
      { title: "Zip to", dataIndex: "destinationZipCode" },
      {
        title: "Car",
        dataIndex: "carPrice",
        render: price => {
          return <span>{formatPrice(price)}</span>;
        },
      },
      {
        title: "SUV",
        dataIndex: "suvPrice",
        render: price => {
          return <span>{formatPrice(price)}</span>;
        },
      },
      {
        title: "Truck",
        dataIndex: "truckPrice",
        render: price => {
          return <span>{formatPrice(price)}</span>;
        },
      },
      {
        title: "Heavy Truck",
        dataIndex: "truckHeavyPrice",
        render: price => {
          return <span>{formatPrice(price)}</span>;
        },
      },
      !inherited &&
        !defaultScenario && {
          title: "",
          dataIndex: "actions",
          render: (text, row) => {
            if (disabled) {
              return null;
            }

            return (
              <MoreMenu
                onItemClick={key => onMenuItemClick(key, row)}
                items={[
                  {
                    key: "edit",
                    icon: <EditOutlined />,
                    label: "Edit",
                    authority: "PricingService.PricingSettings.Update",
                  },
                  {
                    key: "delete",
                    icon: <DeleteOutlined />,
                    label: "Delete",
                    authority: "PricingService.PricingSettings.Update",
                  },
                ]}
              />
            );
          },
        },
    ];
  }, [inherited, disabled, defaultScenario, onMenuItemClick]);

  useEffect(() => {
    if (editingRow === null || filledRef.current !== null) {
      return;
    }

    filledRef.current = editingRow.id;

    formRef.current.setFieldsValue({
      zipFrom: editingRow.originZipCode,
      zipTo: editingRow.destinationZipCode,
      direction: "",
      carPrice: editingRow.carPrice,
      suvPrice: editingRow.suvPrice,
      truckPrice: editingRow.truckPrice,
      truckHeavyPrice: editingRow.truckHeavyPrice,
      priceType: editingRow.priceType,
    });
  }, [editingRow]);

  useEffect(() => {
    if (formRef.current !== null && modalOpen && editingRow === null) {
      formRef.current.setFieldsValue({
        direction: FixedByZipDirectionType.OneWay,
        priceType: FixedByZipPriceType.Fixed,
      });
    }
  }, [modalOpen, editingRow]);

  useEffect(() => {
    const { page: lastCheckedPage, pageSize: lastCheckedPageSize } = lastSortingConfigFetched.current;

    if (lastCheckedPage === page && lastCheckedPageSize === pageSize) {
      return;
    }

    lastSortingConfigFetched.current = {
      page,
      pageSize,
    };

    dispatch({
      type: fetchDealerStrategyByType[DealerStrategiesEnum.FixedByZip],
      referenceId: strategy.referenceId,
      page,
      pageSize,
    });
  }, [page, pageSize, strategy.referenceId]);

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
        }}
      >
        <Button disabled={disabled} onClick={onAddRow}>
          <PlusOutlined />
          {defaultScenario ? "Configure" : "Add row"}
        </Button>
      </div>
      <TableWithPagination
        page={page}
        pageSize={pageSize}
        totalItems={totalItems}
        dataSource={dataSource}
        columns={columns}
        onPageChange={page => setPage(page)}
        onPageSizeChange={pageSize => setPageSize(pageSize)}
      />

      <Modal
        title={`${editingRow === null ? "New" : "Edit"} row`}
        visible={modalOpen}
        afterClose={resetFormFields}
        onCancel={handleCreateCancel}
        footer={
          <div
            style={{
              display: "flex",
              flexDirection: "row-reverse",
              gap: "8px",
            }}
          >
            <Button type="primary" loading={loading} onClick={handleConfirm}>
              Save changes
            </Button>
            <Button onClick={handleCreateCancel}>Cancel</Button>
          </div>
        }
      >
        {defaultScenario && (
          <AlertWithBorder type="info" message="Configuring a new row will delete the current ones." />
        )}
        <Form layout="vertical" ref={formRef}>
          <Row gutter={16}>
            <Col lg={12}>
              <ZipCodeInput
                formRef={formRef}
                customValidator={validateZipCode}
                formItemProps={{
                  label: "Zip from",
                  name: "zipFrom",
                }}
                inputProps={{
                  maxLength: 5,
                  onChange: setZipFrom,
                }}
              />
            </Col>
            <Col lg={12}>
              <ZipCodeInput
                formRef={formRef}
                customValidator={validateZipCode}
                formItemProps={{
                  label: "Zip to",
                  name: "zipTo",
                }}
                inputProps={{
                  maxLength: 5,
                  onChange: setZipTo,
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col lg={24}>
              <Form.Item label="Price Type" name="priceType">
                <Select>
                  {Object.keys(FixedByZipPriceTypeMap).map(priceType => (
                    <Option key={priceType} value={parseInt(priceType)}>
                      {FixedByZipPriceTypeMap[priceType]}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          {showDirectionField && (
            <Row>
              <Col lg={24}>
                <Form.Item label="Direction" name="direction" rules={[{ required: showDirectionField }]}>
                  <Select>
                    {Object.keys(FixedByZipDirectionTypeMap).map(direction => (
                      <Option key={direction} value={parseInt(direction)}>
                        {FixedByZipDirectionTypeMap[direction]}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          )}

          <Row gutter={16}>
            <Col lg={12}>
              <Form.Item label="Car Price" name="carPrice" rules={[{ required: true }]}>
                <InputNumber
                  min={0}
                  step={1}
                  formatter={value => numberToPrice(value, "$ ")}
                  parser={value => value.replace(/\$\s?|(,*)/g, "")}
                />
              </Form.Item>
            </Col>
            <Col lg={12}>
              <Form.Item label="SUV Price" name="suvPrice" rules={[{ required: true }]}>
                <InputNumber
                  min={0}
                  step={1}
                  formatter={value => numberToPrice(value, "$ ")}
                  parser={value => value.replace(/\$\s?|(,*)/g, "")}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col lg={12}>
              <Form.Item label="Truck Price" name="truckPrice" rules={[{ required: true }]}>
                <InputNumber
                  min={0}
                  step={1}
                  formatter={value => numberToPrice(value, "$ ")}
                  parser={value => value.replace(/\$\s?|(,*)/g, "")}
                />
              </Form.Item>
            </Col>
            <Col lg={12}>
              <Form.Item label="Heavy Truck Price" name="truckHeavyPrice" rules={[{ required: true }]}>
                <InputNumber
                  min={0}
                  step={1}
                  formatter={value => numberToPrice(value, "$ ")}
                  parser={value => value.replace(/\$\s?|(,*)/g, "")}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Delete"
        okText="Delete"
        visible={deletingRow !== null}
        onOk={handleDelete}
        onCancel={handleCancelDelete}
        confirmLoading={loadingDelete}
      >
        <span>Are you sure you want to delete this row?</span>
      </Modal>
    </div>
  );
};

export default connect(({ configs }) => ({
  configPerm: configs.configPerm,
}))(FixedByZip);
